import capitalize from 'lodash/capitalize';
import { useTranslation } from 'next-i18next';
import React from 'react';
import styled from 'styled-components';
import { BsButton } from '@hotelplan/core.basis.bs-button';
import { BsIcon } from '@hotelplan/core.basis.bs-icon';
import { BsLink } from '@hotelplan/core.basis.bs-link';
import { formatPrice } from '@hotelplan/fdr.lib.format.format-price';
import { useTransfer } from '@hotelplan/fdr.lib.router.push';
import { FdrRoute } from '@hotelplan/fdr.regular.basis.fdr-route';
import { FdrTextBlock } from '@hotelplan/fdr.regular.basis.fdr-text-block';
import { FdrTransferInfo } from '@hotelplan/fdr.regular.basis.fdr-transfer-info';
import { FdrTripAdvisorInfo } from '@hotelplan/fdr.regular.fusion.fdr-trip-advisor';
import { mapFeatureNameToIcon } from '@hotelplan/libs.mappers';
import { formatDate, parseDateStr } from '@hotelplan/libs.utils';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import { WishlistCompareNotSelectedItemInfo } from 'components/domain/wishlist/WishlistCompareNotSelectedItemInfo';
import { useWishlistSelectedItemsContext } from 'fdr/components/candidate/fdr-wishlist/fdr-wishlist-selected-items-context';
import { FdrOfferBreadcrumbs } from 'fdr/components/local/fdr-offer';
import { mapFdrOrlFlightToIFdrFlightSectionProps } from 'fdr/components/local/fdr-orl/fdr-orl.mappers';
import {
  FdrProductCardImage,
  FdrProductCardRating,
  FdrProductCardSubTitle,
  FdrProductCardTitle,
  FdrTopOffer,
} from 'fdr/components/local/fdr-product-card/fdr-product-card.styles';
import { FdrWishlistItemFragment } from 'fdr/schemas/fragment/wishlist/fdr-wishlist.generated';
import FdrWishlistCompareItemFeatures from './fdr-wishlist-compare-item-features';
import {
  CardCtaWrapper,
  CardFeaturesWrapper,
  CardPriceWrapper,
  CardWrapper,
  DateWrapper,
  FlightSectionShortWrapper,
  IncludedInPriceHeader,
  IncludedInPriceWrapper,
  PriceWrapper,
  RatingWrapper,
  RoomsWrapper,
  RoomTypesWrapper,
  SectionHeader,
  SelectedItemsWrapper,
  TransferInfoWrapper,
  TravelTypeIconWrapper,
  TravelTypeWrapper,
  TripAdvisorWrapper,
  WishlistCompareItemFeaturesWrapper,
  WishlistCompareItemsDesktopWrapper,
  WishlistImageWrapper,
  WishlistItemOverlayWrapper,
  WishlistORLLinkContainer,
  WishlistProductTravelType,
  WishlistRatingCardWrapper,
} from './fdr-wishlist-compare-items-desktop.styles';
import { COMPARE_ITEMS_COUNT_DESKTOP } from './fdr-wishlist.constants';
import { mapWishlistSelectedItemToCompareItem } from './fdr-wishlist.utils';

const EmptyColumnWrapper = styled.div(
  sx2CssThemeFn({
    width: `calc(${100 / 3}% - 32px)`,
    mx: 3,
    p: 0,
  })
);

const EmptyColumn: React.FC<{ index: number; noContent?: boolean }> = ({
  index,
  noContent = false,
}) => {
  const { items, selectedItems } = useWishlistSelectedItemsContext();

  const canShowNotSelectedInfo =
    !items || items.length < COMPARE_ITEMS_COUNT_DESKTOP;

  // NOTE: We should show the info box for non-selected items only to the first empty item.
  const firstEmptyItemIndex = selectedItems.findIndex(item => !item);

  return (
    <EmptyColumnWrapper>
      {canShowNotSelectedInfo && index === firstEmptyItemIndex && !noContent ? (
        <WishlistCompareNotSelectedItemInfo />
      ) : null}
    </EmptyColumnWrapper>
  );
};

const FdrWishlistCompareItemsDesktop: React.FC<{
  onCheckout: (item: FdrWishlistItemFragment) => void;
}> = props => {
  const { onCheckout } = props;
  const { transfer } = useTransfer();
  const [t, i18n] = useTranslation(['common', 'forms']);

  const { selectedItems, updateItem } = useWishlistSelectedItemsContext();

  const showTransferInfo = selectedItems.some(
    item =>
      item?.trackedOffer.latest?.transferPrice ||
      item?.trackedOffer.latest?.transferIncluded
  );

  const TOP_HOTEL_OFFER_ID = 'hotel_top';

  const selectedItemsToShow = selectedItems.map(
    mapWishlistSelectedItemToCompareItem
  );

  return (
    <WishlistCompareItemsDesktopWrapper>
      <SelectedItemsWrapper>
        {selectedItemsToShow.map((item, i) => {
          if (!item) {
            return <EmptyColumn key={`empty-column-${i}`} index={i} />;
          }

          const topOfferFeatures = item.summaryFeatureGroups?.filter(
            feature => feature.id === TOP_HOTEL_OFFER_ID
          );

          return (
            <CardWrapper key={`card-header-${i}`}>
              {item.productTypeData && (
                <TravelTypeWrapper>
                  <WishlistProductTravelType>
                    {item.productTypeData.map((type, y) => {
                      return (
                        <React.Fragment key={y}>
                          <TravelTypeIconWrapper>
                            <BsIcon
                              name={type.icon}
                              className="travel-type-icon"
                            />
                            {t(type.title)}
                          </TravelTypeIconWrapper>
                          {item.productTypeData.length - 1 !== y && (
                            <div className="delimiter"> + </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </WishlistProductTravelType>
                  {topOfferFeatures?.length ? (
                    <FdrTopOffer>
                      {topOfferFeatures.map((feature, index) => {
                        return (
                          <React.Fragment key={index}>
                            <span>
                              <BsIcon
                                name={mapFeatureNameToIcon(feature.id)}
                                size="sm"
                              />
                              {t(feature.name)}
                            </span>
                          </React.Fragment>
                        );
                      })}
                    </FdrTopOffer>
                  ) : null}
                </TravelTypeWrapper>
              )}
              {item.image && (
                <FdrRoute link={item.link}>
                  <WishlistImageWrapper>
                    <FdrProductCardImage image={item.image} />
                  </WishlistImageWrapper>
                </FdrRoute>
              )}
              <WishlistRatingCardWrapper>
                {item.rating > 0 && (
                  <RatingWrapper>
                    <FdrProductCardRating
                      maxRating={5}
                      rating={item.rating}
                      ratingContent={<BsIcon name="hotelplanbewertung" />}
                      halfWayRatingContent={
                        <BsIcon name="hotelplanbewertung-outlined" />
                      }
                    />
                  </RatingWrapper>
                )}
                {item.name && (
                  <FdrProductCardTitle>
                    <FdrRoute link={item.link}>
                      <a>{item.name}</a>
                    </FdrRoute>
                  </FdrProductCardTitle>
                )}
                <FdrProductCardSubTitle>
                  <FdrOfferBreadcrumbs
                    className="bread-crumbs"
                    breadcrumbs={item.breadcrumbs}
                  />
                </FdrProductCardSubTitle>
              </WishlistRatingCardWrapper>
            </CardWrapper>
          );
        })}
      </SelectedItemsWrapper>
      <SelectedItemsWrapper>
        {selectedItemsToShow.map((item, i) => {
          if (!item) {
            return (
              <EmptyColumn key={`empty-column-${i}`} index={i} noContent />
            );
          }

          return (
            <CardWrapper key={`card-flight-${i}`}>
              <SectionHeader>
                <BsIcon name={'flug'} className="wishlist-card-flight-icon" />
                {t('forms:returnFlight.radioInput')}
              </SectionHeader>
              {item.forwardFlight && (
                <FlightSectionShortWrapper
                  {...mapFdrOrlFlightToIFdrFlightSectionProps(
                    item.forwardFlight
                  )}
                />
              )}
              {item.returnFlight && (
                <FlightSectionShortWrapper
                  {...mapFdrOrlFlightToIFdrFlightSectionProps(
                    item.returnFlight
                  )}
                />
              )}
              {!item.forwardFlight && !item.returnFlight && (
                <p>{t('forms:noFlight.title')}</p>
              )}
            </CardWrapper>
          );
        })}
      </SelectedItemsWrapper>
      <SelectedItemsWrapper>
        {selectedItemsToShow.map((item, i) => {
          if (!item) {
            return (
              <EmptyColumn key={`empty-column-${i}`} index={i} noContent />
            );
          }

          return (
            <CardWrapper key={`card-rooms-${i}`}>
              <SectionHeader>
                <BsIcon name={'hotel'} className="wishlist-card-flight-icon" />
                {t('common:HOTEL')},{' '}
                {t('nights.count', { count: item.duration })}
              </SectionHeader>
              <DateWrapper>
                <div>
                  {`${capitalize(
                    formatDate(
                      parseDateStr(item.departureDate),
                      'EE dd.MM.yyyy',
                      i18n.language
                    )
                  )} -`}
                </div>
                <div>
                  {capitalize(
                    formatDate(
                      parseDateStr(item.returnDate),
                      'EE dd.MM.yyyy',
                      i18n.language
                    )
                  )}
                </div>
              </DateWrapper>
              {item.rooms.map((room, y) => {
                return (
                  <RoomsWrapper key={y}>
                    <span className="wishlist-rooms">
                      {t('common:room')} {y + 1}
                    </span>
                    <p className="wishlist-rooms-description">
                      {room.roomDescription}, {room.mealDescription}
                    </p>
                    <RoomTypesWrapper>
                      <li className="room-type">
                        {t('common:adult.count', { count: room.adults })}
                        {(room?.childrenDobs || []).length > 0 &&
                          `, ${t('common:child.count', {
                            count: room.childrenDobs.length,
                          })}`}
                      </li>
                    </RoomTypesWrapper>
                  </RoomsWrapper>
                );
              })}
            </CardWrapper>
          );
        })}
      </SelectedItemsWrapper>
      <SelectedItemsWrapper>
        {selectedItemsToShow.map((item, i) => {
          if (!item) {
            return (
              <EmptyColumn key={`empty-column-${i}`} index={i} noContent />
            );
          }

          return (
            <CardPriceWrapper key={`card-info-${i}`}>
              <PriceWrapper>
                {t('common:perPerson.from')}
                <div className="price">{formatPrice(item.pricePerPerson)}</div>
              </PriceWrapper>
              <PriceWrapper>
                {t('common:totalPrice.from')}
                <div className="price">{formatPrice(item.priceTotal)}</div>
              </PriceWrapper>
            </CardPriceWrapper>
          );
        })}
      </SelectedItemsWrapper>
      {showTransferInfo && (
        <SelectedItemsWrapper>
          {selectedItemsToShow.map((item, i) => {
            if (!item) {
              return (
                <EmptyColumn key={`empty-column-${i}`} index={i} noContent />
              );
            }

            return (
              <CardWrapper key={`card-info-${i}`}>
                <TransferInfoWrapper>
                  <BsIcon name="transfer" className="wishlist-feature-icon" />
                  {item.transferPrice || item.transferIncluded ? (
                    <FdrTransferInfo
                      price={item.transferPrice}
                      transferIncluded={item.transferIncluded}
                    />
                  ) : (
                    t('wishlist:transfer.notAvailable.text')
                  )}
                </TransferInfoWrapper>
              </CardWrapper>
            );
          })}
        </SelectedItemsWrapper>
      )}
      <SelectedItemsWrapper>
        {selectedItemsToShow.map((item, i) => {
          if (!item) {
            return (
              <EmptyColumn key={`empty-column-${i}`} index={i} noContent />
            );
          }

          return (
            <CardFeaturesWrapper key={`card-info-${i}`}>
              <WishlistCompareItemFeaturesWrapper>
                <FdrWishlistCompareItemFeatures item={item} />
              </WishlistCompareItemFeaturesWrapper>
              {item?.includedInPrice && (
                <IncludedInPriceWrapper>
                  <IncludedInPriceHeader>
                    <BsIcon name="money-wallet" />
                    {item.includedInPrice.title}
                  </IncludedInPriceHeader>
                  <FdrTextBlock
                    title={''}
                    text={item.includedInPrice.text}
                    className="included-in-price-text-block"
                  />
                </IncludedInPriceWrapper>
              )}
              {item.taRating > 0 && item.taReviews > 0 && (
                <TripAdvisorWrapper>
                  <FdrTripAdvisorInfo
                    rating={item.taRating}
                    count={item.taReviews}
                    label={t('common:tripAdvisorShort.label')}
                    className="card-trip-advisor"
                  />
                </TripAdvisorWrapper>
              )}
            </CardFeaturesWrapper>
          );
        })}
      </SelectedItemsWrapper>
      <SelectedItemsWrapper>
        {selectedItemsToShow.map((item, i) => {
          if (!item) {
            return (
              <EmptyColumn key={`empty-column-${i}`} index={i} noContent />
            );
          }

          return (
            <CardCtaWrapper key={`cta-${i}`} className="card-cta">
              <BsButton
                className="card-book-button"
                onClick={e => {
                  e.preventDefault();
                  onCheckout(
                    selectedItems.find(
                      selectedItem => selectedItem.product.id === item.productId
                    )
                  );
                }}
              >
                {t('common:book')}
              </BsButton>

              <WishlistORLLinkContainer>
                <BsLink
                  href="#"
                  testId={'product-link'}
                  onClick={e => {
                    e.preventDefault();
                    transfer(item.productLink);
                  }}
                >
                  {t('common:checkOffer')}
                  <BsIcon name="chevron-long-right" />
                </BsLink>
              </WishlistORLLinkContainer>
            </CardCtaWrapper>
          );
        })}
      </SelectedItemsWrapper>
      {selectedItems.map((item, i) => {
        if (!item) return null;

        return (
          <WishlistItemOverlayWrapper
            key={`overlay-${i}`}
            item={item}
            index={i}
            onAccept={acceptedOffer => {
              updateItem(acceptedOffer);
            }}
          />
        );
      })}
    </WishlistCompareItemsDesktopWrapper>
  );
};

export default FdrWishlistCompareItemsDesktop;
